<template>
  <v-dialog :value="value" max-width="560px" persistent @input="$emit('input', $event)">
    <v-card v-if="value">
      <v-card-title class="headline">{{
        existing ? "Override margin · PO " + poLabel : "Isi margin untuk PO tanpa margin CRM"
      }}</v-card-title>
      <v-card-text class="pb-0">
        <div class="bm-tiny mb-4">
          <template v-if="existing">
            {{ current.customer || "—" }}<span v-if="current.title"> · {{ current.title }}</span>
            <span v-if="current.updatedAt">
              · diubah {{ current.updatedBy || "—" }}, {{ formatDateTime(current.updatedAt) }}</span
            >
          </template>
          <template v-else>
            Budget = subtotal PO × (1 − margin). Dipakai hanya selama CRM belum punya margin; begitu
            CRM terisi, angka CRM yang menang.
          </template>
        </div>

        <v-alert v-if="crmWins" type="info" text dense class="mb-4">
          CRM kini punya margin <strong>{{ formatPct(current.currentMarginPercent) }}</strong> ({{
            marginSourceLabel(current.currentMarginSource)
          }}). Override ini <strong>tidak dipakai lagi</strong>; aman dihapus.
        </v-alert>

        <v-autocomplete
          v-if="!existing && !po"
          v-model="form.crmPoId"
          :items="candidates"
          :loading="loadingCandidates"
          item-value="crmPoId"
          :item-text="candidateText"
          label="PO CRM"
          hint="Hanya PO tanpa margin yang muncul. Cari dengan nomor PO."
          persistent-hint
          outlined
          dense
          class="mb-4"
          @change="onPick"
        ></v-autocomplete>
        <v-text-field
          v-else-if="!existing"
          :value="poLabel + (po.customer ? ' · ' + po.customer : '')"
          label="PO CRM"
          outlined
          dense
          readonly
          class="mb-4"
        ></v-text-field>

        <v-row dense class="mb-2">
          <v-col cols="6">
            <div class="bm-tiny">Subtotal PO (CRM)</div>
            <div class="bm-num subtitle-1">Rp {{ formatIdr(subtotal) }}</div>
          </v-col>
          <v-col cols="6">
            <div class="bm-tiny">
              {{ existing ? "Budget yang dipakai sekarang" : "Margin CRM saat ini" }}
            </div>
            <div v-if="existing" class="bm-num subtitle-1">
              Rp {{ formatIdr(current.currentBudgetIdr) }}
              <span class="bm-tiny"
                >({{ marginSourceLabel(current.currentMarginSource) }}
                {{ formatPct(current.currentMarginPercent) }})</span
              >
            </div>
            <div v-else class="subtitle-1 grey--text">kosong</div>
          </v-col>
        </v-row>

        <v-row dense align="start">
          <v-col cols="5">
            <v-text-field
              v-model="form.marginPercent"
              label="Margin"
              suffix="%"
              type="number"
              min="0"
              max="99.99"
              step="0.01"
              hint="0–99, boleh desimal"
              persistent-hint
              outlined
              dense
              autofocus
            ></v-text-field>
          </v-col>
          <v-col cols="7">
            <div class="bm-preview" :class="crmWins ? 'bm-preview--off' : 'bm-preview--on'">
              <div class="bm-tiny">
                {{ crmWins ? "Budget bila override dipakai" : "Budget hasil" }}
              </div>
              <div class="bm-num subtitle-1 font-weight-medium" :class="{ 'grey--text': crmWins }">
                Rp {{ formatIdr(previewBudget) }}
              </div>
              <div class="bm-tiny">
                {{
                  crmWins
                    ? "tidak aktif karena CRM sudah terisi"
                    : "= " + formatIdr(subtotal) + " × (1 − " + (form.marginPercent || 0) + " %)"
                }}
              </div>
            </div>
          </v-col>
        </v-row>

        <v-textarea
          v-model="form.reason"
          label="Alasan"
          rows="2"
          auto-grow
          outlined
          dense
          counter="255"
          hint="Wajib. Tersimpan bersama nama Anda dan waktunya."
          persistent-hint
          class="mt-2"
        ></v-textarea>
      </v-card-text>
      <v-card-actions class="px-4 pb-3">
        <v-btn v-if="existing" text color="error" :loading="deleting" @click="remove"
          >Hapus override</v-btn
        >
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Batal</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="!valid" @click="save">{{
          existing ? "Simpan" : "Simpan & hitung ulang PO"
        }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatDateTime, formatIdr, formatPct, marginSourceLabel } from "@/utils/budgetMonitor";

/** Add / edit / delete a margin override. `po` = a list row (crmPoId, poNumber, customer, subtotalIdr); `override` = an existing MarginOverrideDto. */
export default {
  name: "budget-override-dialog",
  props: {
    value: Boolean,
    po: { type: Object, default: null },
    override: { type: Object, default: null },
  },
  data: () => ({
    form: { crmPoId: null, marginPercent: null, reason: "" },
    candidates: [],
    loadingCandidates: false,
    picked: null,
    saving: false,
    deleting: false,
  }),
  computed: {
    existing() {
      return !!this.override;
    },
    current() {
      return this.override || {};
    },
    source() {
      return this.override || this.po || this.picked || {};
    },
    poLabel() {
      return this.source.poNumber || (this.form.crmPoId ? "#" + this.form.crmPoId : "");
    },
    subtotal() {
      return this.source.subtotalIdr || 0;
    },
    crmWins() {
      return (
        this.existing &&
        this.current.currentMarginSource &&
        this.current.currentMarginSource !== "OVERRIDE" &&
        this.current.currentMarginSource !== "NONE"
      );
    },
    previewBudget() {
      const m = Number(this.form.marginPercent);
      if (Number.isNaN(m)) return null;
      return Number(this.subtotal) * (1 - m / 100);
    },
    valid() {
      const m = Number(this.form.marginPercent);
      return (
        !!this.form.crmPoId &&
        this.form.marginPercent !== null &&
        this.form.marginPercent !== "" &&
        m >= 0 &&
        m < 100 &&
        !!(this.form.reason || "").trim()
      );
    },
  },
  watch: {
    value(v) {
      if (v) this.reset();
    },
  },
  methods: {
    formatIdr,
    formatPct,
    formatDateTime,
    marginSourceLabel,
    candidateText(p) {
      return `${p.poNumber || "#" + p.crmPoId} · ${p.customer || "—"}${
        p.title ? " · " + p.title : ""
      }`;
    },
    reset() {
      const src = this.override || this.po;
      this.form = {
        crmPoId: src ? src.crmPoId : null,
        marginPercent: this.override ? this.override.marginPercent : null,
        reason: this.override ? this.override.reason || "" : "",
      };
      this.picked = null;
      if (!src) this.loadCandidates();
    },
    async loadCandidates() {
      this.loadingCandidates = true;
      try {
        const page = await this.$store.dispatch("budgetMonitor/fetchPos", {
          band: "NO_BUDGET",
          includeClosed: true,
          page: 0,
          size: 100,
        });
        this.candidates = (page && page.items) || [];
      } catch (e) {
        this.candidates = [];
      } finally {
        this.loadingCandidates = false;
      }
    },
    onPick(id) {
      this.picked = this.candidates.find(c => c.crmPoId === id) || null;
    },
    async save() {
      this.saving = true;
      try {
        await this.$store.dispatch("budgetMonitor/saveOverride", {
          crmPoId: this.form.crmPoId,
          marginPercent: this.form.marginPercent,
          reason: this.form.reason.trim(),
        });
        let recomputed = false;
        if (!this.existing) {
          try {
            await this.$store.dispatch("budgetMonitor/recomputePo", this.form.crmPoId);
            recomputed = true;
          } catch (e) {
            recomputed = false;
          }
        }
        this.$store.dispatch("toast", {
          type: "success",
          message: recomputed
            ? "Override tersimpan dan PO dihitung ulang."
            : "Override tersimpan. Berlaku pada perhitungan berikutnya.",
        });
        this.$emit("saved");
        this.$emit("input", false);
      } catch (e) {
        const msg = e && e.response && e.response.data && e.response.data.message;
        this.$store.dispatch("toast", {
          type: "error",
          message: msg || "Gagal menyimpan override",
        });
      } finally {
        this.saving = false;
      }
    },
    async remove() {
      this.deleting = true;
      try {
        await this.$store.dispatch("budgetMonitor/deleteOverride", this.form.crmPoId);
        this.$store.dispatch("toast", {
          type: "success",
          message: "Override dihapus. Berlaku pada perhitungan berikutnya.",
        });
        this.$emit("saved");
        this.$emit("input", false);
      } catch (e) {
        this.$store.dispatch("toast", { type: "error", message: "Gagal menghapus override" });
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.bm-preview {
  padding: 8px 12px;
  border-radius: 4px;
  border: thin solid rgba(0, 0, 0, 0.12);
}
.bm-preview--on {
  background: #e0f2f1;
  border-color: #80cbc4;
}
.bm-preview--off {
  background: #fafafa;
}
</style>
